import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CampaignPerformanceMetrics } from '@/types/reports';
// date-fns format removed - using formatInTz for timezone consistency
import { formatInTz, startOfDayInTz, endOfDayInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { calculateConversionRate, calculateLossRate } from '@/utils/dateFieldStandards';
import { useVisibility } from '@/hooks/useVisibility';

export const useCampaignReport = (startDate: Date, endDate: Date, showActiveOnly: boolean = false, timezone: string = DEFAULT_TIMEZONE) => {
  const { canSeeMarketingCosts } = useVisibility();
  return useQuery({
    enabled: canSeeMarketingCosts,
    queryKey: ['reports', 'campaign-report', formatInTz(startDate, timezone, 'yyyy-MM-dd'), formatInTz(endDate, timezone, 'yyyy-MM-dd'), showActiveOnly, timezone],
    queryFn: async () => {
      // Calculate period (first day of month containing startDate) in configured timezone
      const period = formatInTz(new Date(startDate.getFullYear(), startDate.getMonth(), 1), timezone, 'yyyy-MM-dd');

      // Get all campaigns with lead source
      let query = supabase
        .from('campaigns')
        .select(`
          id,
          name,
          external_campaign_id,
          tracking_number,
          is_active,
          budget,
          lead_source_id,
          lead_sources (name)
        `)
        .order('name');

      if (showActiveOnly) {
        query = query.eq('is_active', true);
      }

      const { data: campaigns, error: campaignsError } = await query;
      if (campaignsError) throw campaignsError;

      // Get daily spend data for the date range (using spend_date in configured timezone)
      const { data: dailySpend, error: dailySpendError } = await supabase
        .from('campaign_daily_spend')
        .select('campaign_id, actual_cost, impressions, clicks, conversions')
        .gte('spend_date', formatInTz(startDate, timezone, 'yyyy-MM-dd'))
        .lte('spend_date', formatInTz(endDate, timezone, 'yyyy-MM-dd'));

      if (dailySpendError) throw dailySpendError;

      // Aggregate daily spend by campaign
      const campaignSpendMap = new Map<string, { actual_cost: number; impressions: number; clicks: number; conversions: number }>();
      dailySpend?.forEach(ds => {
        const existing = campaignSpendMap.get(ds.campaign_id) || { actual_cost: 0, impressions: 0, clicks: 0, conversions: 0 };
        campaignSpendMap.set(ds.campaign_id, {
          actual_cost: existing.actual_cost + (ds.actual_cost || 0),
          impressions: existing.impressions + (ds.impressions || 0),
          clicks: existing.clicks + (ds.clicks || 0),
          conversions: existing.conversions + (ds.conversions || 0),
        });
      });

      // Get period costs for budget info
      const { data: periodCosts, error: periodCostsError } = await supabase
        .from('campaign_period_costs')
        .select('campaign_id, budget')
        .eq('period', period);

      if (periodCostsError) throw periodCostsError;

      // Create lookup map for budgets
      const budgetMap = new Map(
        periodCosts?.map(pc => [pc.campaign_id, pc.budget || 0]) || []
      );

      // Get all leads within date range (using created_at in configured timezone)
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select(`
          id,
          lead_number,
          customer_id,
          customer_name,
          status,
          lead_entry_type,
          campaign_id,
          assigned_csr_id,
          webhook_duration,
          job_id,
          converted_at,
          created_at
        `)
        .gte('created_at', startOfDayInTz(startDate, timezone).toISOString())
        .lte('created_at', endOfDayInTz(endDate, timezone).toISOString());

      if (leadsError) throw leadsError;

      // Get ALL jobs data for metrics calculation using RPC to avoid URL length limits
      // When there are many leads (e.g., 855 in January), the .in() query creates a URL
      // that exceeds PostgREST's ~8KB limit. The RPC does the join server-side.
      const { data: jobs, error: jobsError } = await supabase
        .rpc('get_jobs_for_leads_in_range', {
          start_ts: startOfDayInTz(startDate, timezone).toISOString(),
          end_ts: endOfDayInTz(endDate, timezone).toISOString()
        });

      if (jobsError) throw jobsError;

      // Get CSR names for top performers
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name');

      if (profilesError) throw profilesError;

      // Build metrics for each campaign
      const metrics: CampaignPerformanceMetrics[] = campaigns.map(campaign => {
        const campaignLeads = leads?.filter(l => l.campaign_id === campaign.id) || [];
        const webhookLeads = campaignLeads.filter(l => l.lead_entry_type === 'webhook');
        const manualLeads = campaignLeads.filter(l => l.lead_entry_type === 'manual');
        const convertedLeads = campaignLeads.filter(l => l.status === 'converted');
        const lostLeads = campaignLeads.filter(l => l.status === 'lost');
        const openLeads = campaignLeads.filter(l => ['new', 'processing', 'needs_follow_up', 'estimate_sent', 'estimate_accepted'].includes(l.status || ''));

        // All jobs created from campaign leads (bookings)
        const allCampaignJobs = jobs?.filter(j => campaignLeads.some(l => l.id === j.lead_id)) || [];
        const totalBookings = allCampaignJobs.length;

        // Jobs by status
        const completedJobs = allCampaignJobs.filter(j => j.status === 'completed');
        const cancelledJobs = allCampaignJobs.filter(j => j.status === 'cancelled');
        const quotedJobs = allCampaignJobs.filter(j => j.status === 'quote_sent');

        // Paying jobs (paid or partially paid) from completed jobs only
        const payingJobs = completedJobs.filter(j =>
          j.payment_status === 'paid' || j.payment_status === 'partially_paid'
        );
        const payingJobsPercentage = completedJobs.length > 0
          ? (payingJobs.length / completedJobs.length) * 100
          : 0;

        // Revenue from completed jobs
        const totalRevenue = completedJobs.reduce((sum, j) => sum + (j.invoice_total || 0), 0);
        const avgJobValue = completedJobs.length > 0 ? totalRevenue / completedJobs.length : 0;

        // Net profit = sum of jobs.company_profit for completed jobs. The
        // calculate_job_financials trigger already nets parts, tolls, card fees,
        // tech commission AND override commission out of company_profit, so
        // override must not be subtracted again here.
        const netProfit = completedJobs.reduce((sum, j) => sum + (j.company_profit || 0), 0);

        // Conversion rate: converted / total * 100
        const conversionRate = calculateConversionRate(
          convertedLeads.length,
          campaignLeads.length
        );
        const lossRate = calculateLossRate(lostLeads.length, campaignLeads.length);

        // Get budget and actual spend from daily data
        const budget = budgetMap.get(campaign.id) || campaign.budget || 0;
        const spendData = campaignSpendMap.get(campaign.id);
        const actualCost = spendData?.actual_cost || 0;

        // Calculate cost metrics
        const costPerLead = actualCost > 0 && campaignLeads.length > 0 ? actualCost / campaignLeads.length : 0;
        const costPerJob = actualCost > 0 && completedJobs.length > 0 ? actualCost / completedJobs.length : 0;
        const costPerAcquisition = actualCost > 0 && convertedLeads.length > 0 ? actualCost / convertedLeads.length : 0;
        const returnOnAdSpend = actualCost > 0 ? totalRevenue / actualCost : 0;
        const campaignProfit = netProfit - actualCost;

        // Calculate average call duration
        const callDurations = webhookLeads
          .map(l => l.webhook_duration)
          .filter((d): d is number => d !== null && d !== undefined);
        const avgCallDuration = callDurations.length > 0
          ? callDurations.reduce((sum, d) => sum + d, 0) / callDurations.length
          : null;

        // Top CSRs for this campaign
        const csrCounts = new Map<string, number>();
        campaignLeads.forEach(lead => {
          if (lead.assigned_csr_id) {
            csrCounts.set(lead.assigned_csr_id, (csrCounts.get(lead.assigned_csr_id) || 0) + 1);
          }
        });
        const topCsrs = Array.from(csrCounts.entries())
          .map(([csrId, count]) => ({
            csr_name: profiles?.find(p => p.id === csrId)?.full_name || 'Unknown',
            lead_count: count
          }))
          .sort((a, b) => b.lead_count - a.lead_count)
          .slice(0, 3);

        const recentConversions = convertedLeads
          .slice(0, 10)
          .map(l => {
            const job = jobs?.find(j => j.lead_id === l.id);
            return {
              id: l.id,
              lead_number: l.lead_number,
              customer_id: l.customer_id,
              customer_name: l.customer_name,
              job_number: null,
              job_revenue: job?.invoice_total || null,
              converted_at: l.converted_at,
              created_at: l.created_at,
              status: l.status || 'new'
            };
          });

        return {
          campaign_id: campaign.id,
          campaign_name: campaign.name,
          lead_source_id: campaign.lead_source_id,
          lead_source_name: campaign.lead_sources?.name || null,
          external_campaign_id: campaign.external_campaign_id,
          tracking_number: campaign.tracking_number,
          is_active: campaign.is_active,
          total_leads: campaignLeads.length,
          webhook_leads: webhookLeads.length,
          manual_leads: manualLeads.length,
          leads_converted: convertedLeads.length,
          jobs_quoted: quotedJobs.length,
          leads_lost: lostLeads.length,
          leads_open: openLeads.length,
          conversion_rate: conversionRate,
          loss_rate: lossRate,
          total_bookings: totalBookings,
          total_jobs: completedJobs.length,
          jobs_cancelled: cancelledJobs.length,
          paying_jobs_count: payingJobs.length,
          paying_jobs_percentage: payingJobsPercentage,
          total_revenue: totalRevenue,
          avg_job_value: avgJobValue,
          budget,
          actual_cost: actualCost,
          cost_per_lead: costPerLead,
          cost_per_job: costPerJob,
          cost_per_acquisition: costPerAcquisition,
          return_on_ad_spend: returnOnAdSpend,
          net_profit: netProfit,
          campaign_profit: campaignProfit,
          avg_call_duration: avgCallDuration,
          top_csrs: topCsrs,
          recent_conversions: recentConversions
        };
      });

      // Sort by total revenue descending
      return metrics.sort((a, b) => b.total_revenue - a.total_revenue);
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
